import React, { useState } from "react";
import api from "../utils/api";
import {
  EyeIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import CopyValueButton from "../components/CopyValueButton";
import SEO from "../components/SEO";
import { downloadFile } from "../utils/helpers";
import { generateStandardFilename } from "../../shared/utils/fileHelpers";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";

/**
 * Compact copyable result field for the decoded CSR cards.
 *
 * 2026-07-04 tools-wide overhaul (operator-approved), audit items 11
 * and 12 : CSRDecoder previously carried a dead `handleCopyCSR` and a
 * dead local `copyToClipboard` that were never wired to any button, so
 * customers had no copy affordance at all on decoded values. This
 * component wires REAL copy buttons using the state-driven transient
 * "Copied!" pattern (the `handleCopy` / `copyStatus` idiom in
 * src/client/pages/CSRGenerator.jsx is the reference implementation) : no
 * silent copies, no imperative DOM writes.
 *
 * `value` is the exact string placed on the clipboard ; `display`
 * exists so a field can render a friendlier form (for example the Key
 * Size shows "2048 bits" while copying just "2048"). `emphasize`
 * preserves the pre-existing font-medium treatment that the Common
 * Name value alone carried, so this refactor changes behavior (copy
 * buttons) without changing the established visual hierarchy.
 */
function ResultField({
  label,
  value,
  display,
  fieldKey,
  copyStatus,
  onCopy,
  emphasize = false,
}) {
  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-muted uppercase tracking-wider">
          {label}
        </span>
        <CopyValueButton
          label={label}
          value={value}
          fieldKey={fieldKey}
          copyStatus={copyStatus}
          onCopy={onCopy}
        />
      </div>
      <p
        className={`text-heading mt-1 break-all ${
          emphasize ? "font-medium" : ""
        }`}
      >
        {display || value}
      </p>
    </div>
  );
}

export default function CSRDecoder() {
  const [csr, setCSR] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  // Transient per-field copy feedback : the shared success-gated
  // "Copied!" hook (see src/client/hooks/useCopyFeedback.js for the
  // contract and the timer-overlap bug the hook fixed).
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  // 2026-08-07 download/copy consistency pass (operator-directed) :
  // the exact CSR text that produced `result`, captured at decode time
  // so the Download CSR button always saves what was actually decoded.
  // Downloading the live textarea value instead would let a post-decode
  // edit silently change the saved file while the on-screen decoded
  // details (and the filename's Common Name) still described the old
  // text.
  const [decodedCsr, setDecodedCsr] = useState("");
  const resultsRef = useScrollToResults(result);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    resetCopyStatus();
    setDecodedCsr("");

    // 2026-07-04 operator consistency directive : the disabled gate on
    // the Decode CSR button blocks click-submits of an empty form, and
    // this guard is the backstop for direct form submission (the
    // Enter-key / programmatic path), mirroring the handleSubmit
    // guards on ChainAnalyzer and CertificateMatcher. It replaces the
    // former HTML `required` attribute, whose browser-native bubble
    // did not match the standard error Alert treatment.
    if (!csr.trim()) {
      setError("Please provide a CSR to decode.");
      return;
    }

    setLoading(true);

    try {
      const response = await api.decodeCSR(csr);

      if (response.success && response.data.valid !== false) {
        setResult(response.data);
        // Trimmed : outer whitespace or blank lines around the pasted
        // PEM are paste artifacts, not part of the decoded CSR, and
        // should not travel into the saved file.
        setDecodedCsr(csr.trim());
      } else {
        setError(response.data.error || "The CSR could not be decoded.");
      }
    } catch (err) {
      setError(err.data?.error || err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setCSR("");
    setResult(null);
    setError("");
    resetCopyStatus();
    setDecodedCsr("");
  };

  // 2026-08-07 download/copy consistency pass (operator-directed) :
  // every tool whose result is a savable artifact offers a Download
  // button under the standard {identifier}-{type}-{timestamp} filename
  // (docs/FILENAME_STANDARDS.md) ; this page previously offered
  // per-field copy only, so a customer who pasted a CSR to verify it
  // had no way to save it under a properly-named file. Common Name
  // first, then the e-mail address, because S/MIME requests carry their
  // identity there (the CSR Generator names S/MIME downloads by e-mail
  // the same way).
  //
  // NO LONGER A MIRROR OF fileHelpers.js:generateCertificateFilename,
  // which this used to claim. That function now names a CERTIFICATE
  // through the shared certificateNaming helper, which reads the
  // Subject Alternative Names ; this names a REQUEST, and whether a
  // Certificate Signing Request should be identified by a requested
  // name it has not been issued for is a separate question nobody has
  // answered. Do not point the two at each other again without settling
  // it.
  const handleDownloadCSR = () => {
    const identifier =
      result.subject.commonName ||
      result.subject.emailAddress ||
      result.subject.organizationName ||
      "decoded";
    downloadFile(
      decodedCsr,
      generateStandardFilename(identifier, "csr", "txt")
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : added the
          canonicalUrl so this page matches the site-wide SEO contract
          (see src/client/components/SEO.jsx and the /csr-decoder entry in
          build/generate-static-pages.js). "public key" is lowercase in
          the description : descriptive nouns are lowercase in
          SENTENCES, meta text included, one way everywhere (operator
          ruling 2026-08-28). */}
      <SEO
        title="CSR Decoder - Decode Certificate Signing Request"
        description="Decode and view CSR contents online. Extract subject information, public key details, and signature algorithm from Certificate Signing Requests. Free CSR decoder and validator tool."
        keywords="CSR decoder, decode CSR, CSR viewer, Certificate Signing Request decoder, CSR validator, view CSR contents, CSR information, decode Certificate Signing Request, CSR tool"
        canonicalUrl="https://tools.trustico.com/csr-decoder"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          CSR Decoder
        </h1>
        <p className="text-body">
          Paste a Certificate Signing Request (CSR) to see what is inside it
          before you submit it to a Certificate Authority (CA). The decoder
          shows the subject details, the public key type and size, and the
          signature algorithm, and it verifies that the signature is valid.
          Checking a CSR first catches mistakes in the company or domain details
          that would otherwise end up in the issued SSL Certificate. Both
          SSL/TLS and S/MIME requests are supported.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="card p-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Certificate Signing Request
        </h2>
        <p className="text-body mb-4">
          Paste your Certificate Signing Request (CSR) below to decode and view
          its contents. The CSR must be in PEM format.
        </p>
        <textarea
          id="csr"
          value={csr}
          onChange={(e) => setCSR(e.target.value)}
          className="textarea certificate-textarea mb-4"
          rows="12"
          placeholder="-----BEGIN CERTIFICATE REQUEST-----&#10;...&#10;-----END CERTIFICATE REQUEST-----"
        />

        {error && (
          <Alert type="error" title="Unable to Decode CSR" className="mb-4">
            {error}
          </Alert>
        )}

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            {/* 2026-07-04 operator consistency directive : the primary
                action is disabled until the required input is non-empty
                (the ChainAnalyzer / CertificateMatcher gate pattern) ;
                the handleSubmit guard covers direct form submission. */}
            <ActionButton
              type="submit"
              variant="primary"
              disabled={loading || !csr.trim()}
              loading={loading}
              icon={EyeIcon}
            >
              Decode CSR
            </ActionButton>
            <ActionButton
              type="button"
              variant="outline"
              onClick={handleClear}
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {result && (
        <div ref={resultsRef} className="mt-8 space-y-6 animate-fade-in">
          {/* 2026-07-04 operator consistency directive : the "CSR
              Details" h2 (text-2xl) that used to sit here was the only
              results-group heading in the tool suite and deviated from
              the reference heading hierarchy (h1 text-3xl page title ;
               text-lg font-semibold sections). Sibling pages
              (CSRGenerator, ChainAnalyzer, CertificateMatcher,
              PFXConverter) open their results directly with section
              cards, so the group heading was removed to match. */}

          {/* Subject Information */}
          <div className="card p-6 mb-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Subject Information
            </h2>
            <p className="text-body mb-4">
              The subject information identifies the entity requesting the SSL
              Certificate. This includes the domain name (Common Name),
              organization details, and geographic location.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.subject.commonName && (
                <ResultField
                  label="Common Name (CN)"
                  value={result.subject.commonName}
                  fieldKey="commonName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                  emphasize
                />
              )}

              {result.subject.organizationName && (
                <ResultField
                  label="Organization (O)"
                  value={result.subject.organizationName}
                  fieldKey="organizationName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}

              {result.subject.organizationalUnitName && (
                <ResultField
                  label="Organizational Unit (OU)"
                  value={result.subject.organizationalUnitName}
                  fieldKey="organizationalUnitName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}

              {result.subject.localityName && (
                <ResultField
                  label="Locality (L)"
                  value={result.subject.localityName}
                  fieldKey="localityName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}

              {result.subject.stateOrProvinceName && (
                <ResultField
                  label="State/Province (ST)"
                  value={result.subject.stateOrProvinceName}
                  fieldKey="stateOrProvinceName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}

              {result.subject.countryName && (
                <ResultField
                  label="Country (C)"
                  value={result.subject.countryName}
                  fieldKey="countryName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}

              {result.subject.emailAddress && (
                <ResultField
                  label="E-Mail Address"
                  value={result.subject.emailAddress}
                  fieldKey="emailAddress"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}
            </div>
          </div>

          {/* Public Key Information */}
          <div className="card p-6 mb-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Public Key Information
            </h2>
            <p className="text-body mb-4">
              The public key will be included in the SSL Certificate and used to
              encrypt data sent to your server. The key size and algorithm
              determine the strength of the encryption.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ResultField
                label="Algorithm"
                value={result.publicKey.algorithm}
                fieldKey="algorithm"
                copyStatus={copyStatus}
                onCopy={handleCopy}
              />

              {result.publicKey.bits && (
                <ResultField
                  label="Key Size"
                  value={String(result.publicKey.bits)}
                  display={`${result.publicKey.bits} bits`}
                  fieldKey="bits"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}

              {result.publicKey.curve && (
                <ResultField
                  label="Curve"
                  value={result.publicKey.curve}
                  fieldKey="curve"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
              )}
            </div>
          </div>

          {/* Signature Algorithm */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Signature Information
            </h2>
            <p className="text-body mb-4">
              The signature algorithm is used to sign the CSR with your private
              key, proving that you control the private key associated with the
              public key in the CSR.
            </p>
            <ResultField
              label="Signature Algorithm"
              value={result.signatureAlgorithm}
              fieldKey="signatureAlgorithm"
              copyStatus={copyStatus}
              onCopy={handleCopy}
            />
          </div>

          {/* Validation Status */}
          <div className="mt-6">
            {result.signatureValid !== false ? (
              /* 2026-07-04 tools-wide overhaul (operator-approved), audit
                 item 7 : this is a success STATUS notice, not an error,
                 so the title is Title Case ; since the 2026-08-27 ruling
                 every Alert title is, errors included. */
              <Alert type="success" title="Valid CSR">
                The CSR signature has been verified successfully
              </Alert>
            ) : (
              /* Title Case like every other Alert title (operator
                 ruling 2026-08-27 : UPPERCASE is for the logs, a
                 customer reads Title Case titles and sentences). The
                 body already says the CSR decoded, so the title names
                 only the failure. */
              <Alert type="warning" title="CSR Signature Validation Failed">
                The CSR was decoded successfully but signature verification
                failed. This may indicate a tampered or corrupted CSR, an
                invalid signature, or an unsupported signature algorithm.
                Exercise caution when using this CSR for SSL Certificate
                issuance.
              </Alert>
            )}
          </div>

          {/* 2026-08-07 download/copy consistency pass
              (operator-directed) : centered single-action row (the
              CSRGenerator ZIP-card / DNSStampGenerator copy-row
              placement idiom) saving the decoded CSR - see
              handleDownloadCSR above for the filename rationale. The
              row renders on the failed-signature branch too : the CSR
              decoded successfully there, and the customer may still
              need the file (for example to show their Certificate
              Authority what was rejected). */}
          <div className="flex justify-center">
            <ActionButton
              onClick={handleDownloadCSR}
              variant="outline"
              icon={ArrowDownTrayIcon}
            >
              Download CSR
            </ActionButton>
          </div>
        </div>
      )}
    </div>
  );
}
